/* Excel (xlsx) import/export of document registers via SheetJS. */
import * as XLSX from 'xlsx'

import { colHeader } from '@/i18n/columns'
import { t, type Lang } from '@/i18n/lang'
import { lifeLabel } from '@/ui/primitives'

/* eslint-disable @typescript-eslint/no-explicit-any */

/* Заголовки — канонические русские ключи словаря колонок: файл выгружается на
   языке интерфейса тем же словарём, каким переводятся экранные таблицы.
   Значения остаются доменными данными и не переводятся. */
const COL_KEYS: [string, (d: any) => unknown][] = [
  ['№ документа', (d) => d.no],
  ['Наименование', (d) => d.title],
  /* Английское название — колонка бланка входящего трансмиттала
     (Vendor Document Title English). Пусто там, где его не завели. */
  ['Наименование (EN)', (d) => d.titleEn ?? ''],
  /* Титул и объект — свойства СТРОКИ (V90): реестр по контракту идёт по
     нескольким объектам, и без этих колонок выгруженный реестр терял их, а
     обратная загрузка — переставляла строки в объект реестра. */
  ['Титул', (d) => d.titul ?? ''],
  ['Объект', (d) => d.area ?? ''],
  ['Дисциплина', (d) => d.disc],
  ['Тип', (d) => d.type],
  ['Ревизия', (d) => d.rev],
  /* Лист — часть номера стадии BD; в выгрузке стоит рядом с ревизией. */
  ['Лист', (d) => d.sheet ?? ''],
  ['Назначение', (d) => d.purpose],
  ['Код', (d) => d.code ?? ''],
  /* Состояние — названием, как на экране, а не внутренним кодом: файл уходит
     ГИПу, заказчику и подрядчику, и «review» в колонке статуса заставляет
     переписывать её руками. Обратная загрузка того же файла понимает и
     название, и код (LIFE_CODES). */
  ['Статус', (d) => (d.life ? lifeLabel(String(d.life)) : '')],
  ['План', (d) => d.plan],
  ['Прогноз', (d) => d.forecast],
  ['Факт', (d) => d.fact ?? ''],
  /* Сроки по стадиям круга: план выдачи, согласования и приёмки. Их ведут
     реестром, и без них файл описывает срок документа целиком — а он бывает
     разным на каждой стадии. */
  ['План IFR', (d) => d.planIfr ?? ''],
  ['План IFA', (d) => d.planIfa ?? ''],
  ['План AFD', (d) => d.planAfd ?? ''],
  /* Прогноз и факт по каждой стадии: по выгруженному реестру ведут
     совещание, и «что обещали» без «что ожидается» и «что сделано» на нём
     бесполезно. Раньше их собирали, открывая карточки по одной. */
  ['Прогноз IFR', (d) => d.forecastIfr ?? ''],
  ['Прогноз IFA', (d) => d.forecastIfa ?? ''],
  ['Прогноз AFD', (d) => d.forecastAfd ?? ''],
  ['Факт IFR', (d) => d.factIfr ?? ''],
  ['Факт IFA', (d) => d.factIfa ?? ''],
  ['Факт AFD', (d) => d.factAfd ?? ''],
  /* Номер подачи: круг замечаний щёлкает версию, и по ней в переписке
     находят, о какой именно подаче речь. */
  ['Версия', (d) => (d.ver ?? '')],
  ['Контрагент', (d) => d.vendor],
  ['TRM', (d) => d.trm ?? ''],
  ['Реестр', (d) => d.mdr ?? ''],
  ['№ заказчика', (d) => d.mdrRow ?? ''],
]

/* Лист реестра: шапка на языке выгрузки, строка на документ.

   Пустой набор даёт голую шапку, а не пустой лист: этим листом заказчик
   передаёт подрядчику ШАБЛОН реестра, и шапка в нём — единственное, что есть. */
function docsSheet(rows: any[], lang: Lang, attrs: { code: string; title: string }[] = []) {
  /* Проектные атрибуты — колонками справа: их заголовки заданы администратором
     по файлу заказчика, и переводить их незачем — они и есть его слова.
     Выгруженный так реестр грузится обратно вместе с ними. */
  const headers = [...COL_KEYS.map(([ru]) => colHeader(lang, ru)), ...attrs.map((a) => a.title)]
  const toRow = (d: any) => ({
    ...Object.fromEntries(COL_KEYS.map(([ru, pick]) => [colHeader(lang, ru), pick(d)])),
    ...Object.fromEntries(attrs.map((a) => [a.title, d.attrs?.[a.code] ?? ''])),
  })
  const ws = rows.length
    ? XLSX.utils.json_to_sheet(rows.map(toRow), { header: headers })
    : XLSX.utils.aoa_to_sheet([headers])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'MDR')
  return wb
}

export function exportDocs(rows: any[], filename = 'registry.xlsx', lang: Lang = 'ru',
                           attrs: { code: string; title: string }[] = []): void {
  XLSX.writeFile(docsSheet(rows, lang, attrs), filename)
}

/**
 * Тот же лист, но файлом в памяти: его прикладывают к трансмитталу, а не
 * сохраняют на диск. Скачивать шаблон, чтобы тут же выбрать его в диалоге
 * загрузки, — лишний шаг, на котором теряется именно тот файл, который ушёл.
 */
export function docsTemplateFile(rows: any[], filename: string, lang: Lang = 'ru',
                                 attrs: { code: string; title: string }[] = []): File {
  const bytes = XLSX.write(docsSheet(rows, lang, attrs), { type: 'array', bookType: 'xlsx' }) as ArrayBuffer
  return new File([bytes], filename,
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

/**
 * Разобранный лист реестра.
 *
 * `lines` — номер строки в самом файле для каждой строки `rows`. Отчёт сервера
 * нумерует строки запроса, а человек ищет строку глазами в Excel: над шапкой
 * стоит название реестра, между строками попадаются пустые, и «строка 7»
 * отчёта указывала не туда. Перевод возможен только здесь: какая строка листа
 * стала какой строкой запроса, знает разбор файла, а не сервер.
 */
export interface DocSheet {
  rows: Record<string, string>[]
  lines: number[]
}

/* Значение ячейки строкой.

   Дата в обычном Excel — это ДАТА, а не текст: без `cellDates` она приходит
   серийным числом («46266.0001…»), уезжает на сервер как есть и отбивается
   построчно на всём файле. Загружалась только собственная выгрузка системы,
   где даты лежат текстом. Приводим к ДД.ММ.ГГГГ — тому виду, который сервер
   и принимает (RegisterCommands.parseDate).

   Составляющие берутся местными getDate/getMonth/getFullYear: SheetJS строит
   дату в местном поясе (полночь по месту), и UTC-составляющие сдвинули бы её
   на сутки назад в поясах восточнее Гринвича. */
function cellText(value: unknown): string {
  if (value == null) return ''
  if (value instanceof Date) {
    const pad = (n: number) => String(n).padStart(2, '0')
    return pad(value.getDate()) + '.' + pad(value.getMonth() + 1) + '.' + value.getFullYear()
  }
  return String(value).trim()
}

/* Колонка номера: по ней и опознаётся шапка. Номер в файле обязателен — в
   реестр загружаются готовые номера, — поэтому строка, где она узнана, и есть
   шапка. Ключи те же, что у разбора строки (IMPORT_COLS). */
const NO_KEYS = ['№ док', 'номер', 'no', 'document', '文件编号']

/**
 * Разбор присланного файла реестра.
 *
 * Шапка не обязана быть первой строкой: заказчик присылает файл с названием
 * реестра, номером договора и пустой строкой над ней — так же, как это уже
 * учитывает разбор справочников. Шапкой считается первая строка, в которой
 * узнаётся колонка номера, всё выше — заголовок файла.
 *
 * Колонки берутся из ШАПКИ, а не из первой строки данных: SheetJS не отдаёт
 * ключ для пустой ячейки, и колонка, пустая в первой строке («Примечание»,
 * «Тег оборудования»), исчезала бы по всему файлу.
 */
export async function parseDocs(file: File): Promise<DocSheet> {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true })
  const first = wb.SheetNames[0]
  const ws = first === undefined ? undefined : wb.Sheets[first]
  if (!ws) return { rows: [], lines: [] }
  const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '' })
  /* Шапка — строка ЗАГОЛОВКОВ, а не всякая строка со словом «номер»: над
     таблицей стоит текст («Номера документов этого реестра: …», «Договор
     №…»), и одного вхождения ключа мало. У шапки колонок много, у строки
     заголовка — одна: по этому они и различаются. */
  const HEAD_MIN_CELLS = 3
  const head = grid.findIndex((row) =>
    row.filter((c) => cellText(c) !== '').length >= HEAD_MIN_CELLS
    && row.some((c) => NO_KEYS.some((k) => cellText(c).toLowerCase().includes(k))))
  if (head < 0) {
    throw new Error('В файле не найдена шапка реестра: нужна колонка «№ документа»')
  }
  const titles = grid[head]!.map((c) => cellText(c))
  // Лист не обязан начинаться с A1: номер строки считается от начала диапазона.
  const origin = XLSX.utils.decode_range(ws['!ref'] ?? 'A1').s.r

  const rows: Record<string, string>[] = []
  const lines: number[] = []
  for (let i = head + 1; i < grid.length; i++) {
    const cells: Record<string, string> = {}
    let filled = false
    grid[i]!.forEach((cell, col) => {
      const title = titles[col]
      const value = cellText(cell)
      if (value) filled = true
      /* Колонка достаётся первому одноимённому столбцу: «Примечание» в
         присланных файлах встречается дважды, и пустой второй затёр бы первый. */
      if (title && cells[title] === undefined) cells[title] = value
    })
    // Пустая строка-разделитель — не строка реестра: отчёт о ней молчит.
    if (!filled) continue
    rows.push(cells)
    lines.push(origin + i + 1)
  }
  return { rows, lines }
}

/* Строка команды создания документа. Незаполненные поля не отправляются:
   сервер отличает «не задано» от «задано пустым» — номер он выделяет сам,
   дисциплину берёт из реестра, тип по умолчанию DR. */
export interface DocRow {
  no?: string
  title?: string
  titleEn?: string
  /** Стадия строки: сервер сверяет её со стадией реестра. */
  stage?: string
  disc?: string
  type?: string
  rev?: string
  sheet?: string
  purpose?: string
  plan?: string
  vendor?: string
  /* Титул и объект — свойства документа, а не реестра: у титула объект один,
     и объект документа берётся из титула, когда он назван. */
  titul?: string
  area?: string
  /** Прогноз и факт: нужны переносу идущего проекта, у нового документа их нет. */
  forecast?: string
  fact?: string
  /** Сроки по стадиям круга — их ведут реестром, а не карточка за карточкой. */
  planIfr?: string
  /* Прогноз по стадиям читается обратно: он есть в выгрузке, и без этого
     повторная загрузка реестра затирала названную подрядчиком дату планом. */
  forecastIfr?: string
  forecastIfa?: string
  forecastAfd?: string
  planIfa?: string
  planAfd?: string
  /* Факт по стадиям: он есть в выгрузке, и без чтения обратно перенос идущего
     проекта терял половину истории — свой же выгруженный реестр возвращался
     без дат сдачи стадий. */
  factIfr?: string
  factIfa?: string
  factAfd?: string
  /** Номер заказчика для документа поставщика: половина шапки листа CRS. */
  mdrRow?: string
  /** Перенос идущего проекта: состояние и код согласования из старого реестра. */
  life?: string
  code?: string
  /* Колонки реестра заказчика, которых нет в схеме: тег оборудования, система,
     гриф, формат. Ключ — код атрибута проекта; сопоставление идёт по заголовку,
     который администратор задал атрибуту. Прежде такие колонки исчезали молча. */
  attrs?: Record<string, string>
}

/* Заголовки в присланном файле не нормированы: колонка сопоставляется по
   вхождению подстроки — так же, как это уже делает импорт матрицы
   рассмотрения. Шаблон выгрузки (exportDocs) даёт ровно эти заголовки на
   любом из трёх языков, поэтому выгруженный реестр загружается обратно без
   правки шапки независимо от языка, на котором его выгрузили. */
/* Порядок важен: колонка сопоставляется по первому вхождению подстроки, и
   «Наименование (EN)» обязано разбираться раньше «Наименования», иначе
   английское название попадёт в русское поле. */
/* Поля, которые разбираются из шапки файла: атрибуты проекта в этот список не
   входят — они приходят набором и кладутся в row.attrs. */
type DocRowField = Exclude<keyof DocRow, 'attrs'>

/* Колонки, которые выгрузка ставит сама: номер подачи, пакет и реестр. Ими
   владеет документооборот, и в загрузке они не значат ничего. */
const SERVICE_COLS = ['версия', 'version', '版本', 'trm', 'реестр', 'register', '登记册']

const IMPORT_COLS: [DocRowField, string[]][] = [
  /* Стадийные сроки разбираются РАНЬШЕ общих: «План IFR» подходит и под
     «план», и колонка ушла бы в общий план вместе со своей стадией. */
  ['factIfr', ['факт ifr', 'fact ifr', 'actual ifr']],
  ['factIfa', ['факт ifa', 'fact ifa', 'actual ifa']],
  ['factAfd', ['факт afd', 'fact afd', 'actual afd', 'факт afc']],
  ['forecastIfr', ['прогноз ifr', 'forecast ifr']],
  ['forecastIfa', ['прогноз ifa', 'forecast ifa']],
  ['forecastAfd', ['прогноз afd', 'forecast afd', 'прогноз afc']],
  ['planIfr', ['план ifr', 'plan ifr', 'ifr план']],
  ['planIfa', ['план ifa', 'plan ifa', 'ifa план']],
  ['planAfd', ['план afd', 'plan afd', 'afd план', 'план afc']],
  /* Пара номеров: у документа поставщика это номер заказчика, и без него
     шапка листа замечаний остаётся наполовину пустой. */
  ['mdrRow', ['№ заказчика', 'номер заказчика', 'company doc', 'customer doc']],
  /* Титул разбирается РАНЬШЕ наименования: английский заголовок колонки —
     «Project title», и «title» забрал бы её себе. */
  ['titul', ['титул', 'project title', '项目名称']],
  /* «Object» — заголовок этой колонки в англоязычной выгрузке клиента, «Area» —
     в книге, которую собирает сервер: файл приходит и оттуда, и оттуда. */
  ['area', ['объект', 'площад', 'area', 'object', '对象', '区域']],
  ['no', ['№ док', 'номер', 'no', 'document', '文件编号']],
  ['titleEn', ['наименование (en)', 'title (en)', 'title english', '名称（英文）']],
  ['title', ['наимен', 'назван', 'title', '名称']],
  /* Стадия строки: в книге реестра она стоит колонкой, заполненной стадией
     реестра. Сервер сверяет её и отбивает чужую — так узнаётся файл, который
     заполняли по шаблону другого реестра. Ключ идёт ДО дисциплины: «стадия» и
     «статус» начинаются одинаково только для человека, но колонка одна. */
  ['stage', ['стадия', 'stage', '阶段']],
  ['disc', ['дисципл', 'disc', '专业']],
  ['type', ['тип', 'type', '类型']],
  ['rev', ['ревиз', 'rev', '版次']],
  ['sheet', ['лист', 'sheet', '图号']],
  ['purpose', ['назнач', 'purpose', '用途']],
  ['plan', ['план', 'plan', '计划']],
  ['forecast', ['прогноз', 'forecast', '预测']],
  ['fact', ['факт', 'fact', 'actual', '实际']],
  /* Перенос идущего проекта: состояние и код приходят из старого реестра. */
  ['life', ['статус', 'состояние', 'life', 'status', '状态']],
  ['code', ['код', 'code', '编码']],
  ['vendor', ['контраг', 'поставщ', 'vendor', 'contractor', '相对方', '承包商', '供应商']],
]

/* Обратный разбор состояния: выгрузка печатает НАЗВАНИЕ («На рассмотрении»),
   а сервер принимает код («review») и на всё прочее отвечает 400. Свой же
   выгруженный реестр обязан загружаться обратно, поэтому название узнаётся
   здесь. Неизвестное значение уходит как есть — решает сервер, а не эта
   таблица. */
/* Первое имя — нынешнее название состояния, дальше — прежние: файл, выгруженный
   до переименования, обязан грузиться и после него. */
const LIFE_CODES: [string, ...string[]][] = [
  ['planned', 'Запланирован'],
  ['received', 'Получен'],
  ['review', 'На рассмотрении'],
  ['crs', 'Замечания CRS'],
  ['reissue', 'Переиздание'],
  /* «Согласован» переехал от approved к afd (V97): согласован — финал схемы
     назначений, а код B остаётся «принят с замечаниями». Файл, выгруженный до
     переезда, со словом «Согласован» теперь грузится как afd — это и есть
     нынешний смысл слова. */
  ['approved', 'Принят с замечаниями'],
  ['accepted', 'Принят · ожидается AFD', 'Принят — ожидается AFD'],
  ['afd', 'Согласован', 'AFD · в работе'],
  ['hold', 'Приостановлен'],
  ['superseded', 'Заменён'],
  ['void', 'Аннулирован'],
]

export function lifeCode(value: string): string {
  const name = value.trim().toLowerCase()
  const hit = LIFE_CODES.find(([code, ...names]) =>
    code === name || names.some((n) => n.toLowerCase() === name))
  return hit ? hit[0] : value
}

export function toDocRow(raw: Record<string, unknown>, attrs: { code: string; title: string }[] = []): DocRow {
  const row: DocRow = {}
  /* Колонка отдаётся одному полю: «Наименование (EN)» подходит и под «наимен»,
     и файл, где есть только английское название, иначе положил бы его в
     русское поле. */
  const taken = new Set<string>()
  for (const [field, keys] of IMPORT_COLS) {
    const hit = Object.keys(raw).find((k) => !taken.has(k) && keys.some((n) => k.toLowerCase().includes(n)))
    if (hit !== undefined) taken.add(hit)
    const value = hit === undefined ? '' : String(raw[hit] ?? '').trim()
    // Пустая ячейка — это отсутствующее поле, а не пустое значение: номер и
    // дисциплину сервер выведет сам.
    if (value) row[field] = field === 'life' ? lifeCode(value) : value
  }
  /* Проектные атрибуты: колонка узнаётся по заголовку, который администратор
     задал атрибуту. Берётся после известных полей — заголовок «Система» не
     должен перебивать «Статус». */
  const extra: Record<string, string> = {}
  for (const a of attrs) {
    const hit = Object.keys(raw).find((k) => !taken.has(k) && k.trim().toLowerCase() === a.title.trim().toLowerCase())
    if (hit === undefined) continue
    taken.add(hit)
    const value = String(raw[hit] ?? '').trim()
    if (value) extra[a.code] = value
  }
  if (Object.keys(extra).length) row.attrs = extra
  return row
}

/**
 * Колонки присланного файла, которые никуда не легли.
 *
 * <p>Заказчик присылает MDR на сорок колонок, система знает двадцать — и
 * остальные исчезали молча. Импорт обязан назвать их: по ним заводят атрибуты
 * проекта, а не второй Excel рядом с реестром.
 */
/**
 * Сводная строка листа: по каждой колонке — первое непустое значение.
 *
 * <p>Разбор колонок смотрел ПЕРВУЮ строку данных, а SheetJS не отдаёт ключ для
 * пустой ячейки: колонка, пустая в первой строке («Примечание», «Тег
 * оборудования»), не попадала ни в предупреждение, ни в атрибуты — и её
 * значения исчезали по всему файлу молча.
 */
export function sampleRow(rows: Record<string, unknown>[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const row of rows) {
    for (const [key, value] of Object.entries(row)) {
      if (out[key] === undefined || String(out[key] ?? '').trim() === '') out[key] = value
    }
  }
  return out
}

export function unknownColumns(raw: Record<string, unknown>, attrs: { code: string; title: string }[] = []): string[] {
  const known = new Set<string>()
  /* Служебные колонки выгрузки: их ставит система, и обратно она их не
     принимает. Молчать о них нельзя было бы, будь они данными заказчика, — но
     это наши же колонки, и предлагать завести под них атрибут проекта значит
     просить человека исправить то, что он не писал. */
  for (const k of Object.keys(raw)) {
    const name = k.trim().toLowerCase()
    if (SERVICE_COLS.some((c) => name === c)) known.add(k)
  }
  for (const [, keys] of IMPORT_COLS) {
    const hit = Object.keys(raw).find((k) => !known.has(k) && keys.some((n) => k.toLowerCase().includes(n)))
    if (hit !== undefined) known.add(hit)
  }
  for (const a of attrs) {
    const hit = Object.keys(raw).find((k) => k.trim().toLowerCase() === a.title.trim().toLowerCase())
    if (hit !== undefined) known.add(hit)
  }
  return Object.keys(raw).filter((k) => !known.has(k) && String(raw[k] ?? '').trim() !== '')
}

/** Шапка сопроводительной формы трансмиттала. */
export interface TrmFormHead {
  id: string
  dir: 'in' | 'out'
  subject: string
  from: string
  to: string
  date: string
  due: string | null
  purpose: string
  status: string
}

/**
 * Сопроводительная форма TRM: шапка трансмиттала и таблица состава.
 * Строится на языке интерфейса; значения — доменные данные как есть.
 */
export function buildTrmForm(trm: TrmFormHead, rows: any[], lang: Lang = 'ru'): XLSX.WorkBook {
  const aoa: unknown[][] = [
    [t(lang, 'Трансмиттал', 'Transmittal'), trm.id],
    [t(lang, 'Тема', 'Subject'), trm.subject],
    [t(lang, 'Направление', 'Direction'), trm.dir === 'in' ? t(lang, 'Входящий', 'Incoming') : t(lang, 'Исходящий', 'Outgoing')],
    [t(lang, 'Отправитель', 'From'), trm.from],
    [t(lang, 'Получатель', 'To'), trm.to],
    [t(lang, 'Дата регистрации', 'Registered'), trm.date],
    [t(lang, 'Срок ответа', 'Due'), trm.due ?? '—'],
    [t(lang, 'Назначение выпуска', 'Purpose'), trm.purpose],
    [t(lang, 'Статус', 'Status'), trm.status],
    [],
    [colHeader(lang, '№ документа'), colHeader(lang, 'Наименование'), colHeader(lang, 'Ревизия'), colHeader(lang, 'Назначение')],
    ...rows.map((d) => [d.no, d.title, d.rev, d.purpose]),
  ]
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = [{ wch: 26 }, { wch: 60 }, { wch: 10 }, { wch: 12 }]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'TRM')
  return wb
}

/* ---- Опись пакета трансмиттала ---------------------------------------- */

/** Строка описи файлов: то, что физически лежит в архиве. */
export interface PackageFileRow {
  doc: string
  title?: string
  rev: string
  ver: number
  kind: string
  name: string
  size: number
  /** Путь внутри архива — по нему получатель находит файл, не гадая. */
  path: string
  /** Пометка недействующей копии, если ревизия уже заменена. */
  notice?: string | null
  /** Лёг ли файл в архив: сбой по одному файлу пакет не роняет, но молчать о нём нельзя. */
  packed: boolean
}

/** Строка описи листов замечаний. */
export interface PackageSheetRow {
  code: string
  doc: string
  rev?: string
  ver?: number
  comments?: number
  status?: string
  path: string
  packed: boolean
}

/**
 * Опись пакета: перечень переданных файлов и перечень переданных листов CRS.
 *
 * <p>Архив без описи — папка с файлами: получатель пересчитывает вложения
 * руками и спорит о том, что именно ему передали. Опись отвечает на это
 * бумагой: что, какой ревизии и версии, каким файлом и по какому пути лежит.
 * Строка, не попавшая в архив, из описи НЕ убирается — она помечается «нет»:
 * молча укоротившаяся опись хуже честной пометки.
 */
export function buildPackageIndex(trm: TrmFormHead, files: PackageFileRow[], sheets: PackageSheetRow[],
                                  lang: Lang = 'ru'): XLSX.WorkBook {
  const yes = t(lang, 'да', 'yes')
  const no = t(lang, 'нет', 'no')
  const head: unknown[][] = [
    [t(lang, 'Трансмиттал', 'Transmittal'), trm.id],
    [t(lang, 'Тема', 'Subject'), trm.subject],
    [t(lang, 'Отправитель', 'From'), trm.from],
    [t(lang, 'Получатель', 'To'), trm.to],
    [t(lang, 'Дата регистрации', 'Registered'), trm.date],
    [t(lang, 'Назначение выпуска', 'Purpose'), trm.purpose],
    [],
  ]

  const filesAoa: unknown[][] = [
    ...head,
    [t(lang, 'Перечень передаваемых файлов', 'Files transmitted'), files.length],
    [
      '№',
      colHeader(lang, '№ документа'),
      colHeader(lang, 'Наименование'),
      colHeader(lang, 'Ревизия'),
      t(lang, 'Версия', 'Version'),
      t(lang, 'Вид файла', 'File role'),
      t(lang, 'Имя файла', 'File name'),
      t(lang, 'Размер, КБ', 'Size, KB'),
      t(lang, 'Путь в архиве', 'Path in archive'),
      t(lang, 'Пометка', 'Notice'),
      t(lang, 'В архиве', 'Packed'),
    ],
    ...files.map((f, i) => [
      i + 1, f.doc, f.title ?? '', f.rev, f.ver, f.kind, f.name,
      Math.max(1, Math.round(f.size / 1024)), f.path, f.notice ?? '', f.packed ? yes : no,
    ]),
  ]
  const wsFiles = XLSX.utils.aoa_to_sheet(filesAoa)
  wsFiles['!cols'] = [{ wch: 5 }, { wch: 26 }, { wch: 44 }, { wch: 9 }, { wch: 8 }, { wch: 14 },
    { wch: 34 }, { wch: 12 }, { wch: 40 }, { wch: 26 }, { wch: 10 }]

  const sheetsAoa: unknown[][] = [
    ...head,
    [t(lang, 'Перечень передаваемых листов CRS', 'CRS sheets transmitted'), sheets.length],
    [
      '№',
      t(lang, 'Лист CRS', 'CRS sheet'),
      colHeader(lang, '№ документа'),
      colHeader(lang, 'Ревизия'),
      t(lang, 'Версия', 'Version'),
      t(lang, 'Замечаний', 'Comments'),
      colHeader(lang, 'Статус'),
      t(lang, 'Путь в архиве', 'Path in archive'),
      t(lang, 'В архиве', 'Packed'),
    ],
    ...sheets.map((c, i) => [
      i + 1, c.code, c.doc, c.rev ?? '', c.ver ?? '', c.comments ?? '', c.status ?? '',
      c.path, c.packed ? yes : no,
    ]),
  ]
  const wsSheets = XLSX.utils.aoa_to_sheet(sheetsAoa)
  wsSheets['!cols'] = [{ wch: 5 }, { wch: 24 }, { wch: 26 }, { wch: 9 }, { wch: 8 }, { wch: 11 },
    { wch: 14 }, { wch: 28 }, { wch: 10 }]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, wsFiles, t(lang, 'Файлы', 'Files'))
  XLSX.utils.book_append_sheet(wb, wsSheets, 'CRS')
  return wb
}

/** Скачивание формы TRM отдельным файлом (кнопка в карточке). */
export function exportTrmForm(trm: TrmFormHead, rows: any[], lang: Lang = 'ru'): void {
  XLSX.writeFile(buildTrmForm(trm, rows, lang), trm.id + '_form.xlsx')
}

/* Журналы (трансмитталы, аудит) выгружаются одной механикой: список пар
   «канонический русский заголовок → значение строки». Заголовок переводится
   тем же словарём, что и экранные таблицы, значения остаются данными. */
type Column<R> = [string, (row: R) => unknown]

export function exportRows<R>(rows: R[], columns: Column<R>[], filename: string, sheet: string, lang: Lang = 'ru'): void {
  const ws = XLSX.utils.json_to_sheet(rows.map((r) =>
    Object.fromEntries(columns.map(([ru, pick]) => [colHeader(lang, ru), pick(r)]))))
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheet)
  XLSX.writeFile(wb, filename)
}

/** Журнал трансмитталов. Направление — словом, а не кодом in/out. */
export function exportTransmittals(rows: any[], filename: string, lang: Lang = 'ru'): void {
  exportRows<any>(rows, [
    ['№ документа', (r) => r.id],
    ['Дата', (r) => r.date],
    ['Содержание', (r) => r.subject],
    ['Источник', (r) => r.dir === 'in' ? t(lang, 'Входящий', 'Incoming') : t(lang, 'Исходящий', 'Outgoing')],
    ['Контрагент', (r) => r.dir === 'in' ? r.from : r.to],
    ['Назначение', (r) => r.purpose],
    ['Документов', (r) => r.docs],
    ['Срок', (r) => r.due ?? '—'],
    ['Статус', (r) => r.status],
    ['Код', (r) => r.code ?? ''],
    /* Подтверждение получения: журнал передач без него отвечает «отправили»,
       а спрашивают «дошло». */
    ['Получен', (r) => r.acknowledged ?? ''],
    ['Получил', (r) => r.acknowledgedBy ?? ''],
  ], filename, 'TRM', lang)
}

/** Журнал аудита. Неизменяемый след: выгрузка — копия, а не выборка «как есть». */
export function exportAudit(rows: any[], filename: string, lang: Lang = 'ru'): void {
  exportRows<any>(rows, [
    ['Дата и время', (r) => r.ts],
    ['Пользователь', (r) => r.user],
    ['Действие', (r) => r.action],
    ['Объект', (r) => r.obj],
    ['Описание', (r) => r.target],
    /* «Было → стало»: без него выгрузка отвечает «что-то изменили», а на
       разборе спрашивают, каким значение было и каким стало. */
    ['Было → стало', (r) => r.changes ?? ''],
    ['Источник', (r) => r.ip],
  ], filename, 'AUDIT', lang)
}

/** Лист CRS в форме ответа GET /crs-sheets/{code} (см. CrsSheet.SheetForm). */
export interface CrsSheetData {
  code: string
  doc: string
  title: string | null
  rev: string | null
  ver?: number | null
  incoming: string
  outgoing: string | null
  reviewCode: string
  status: string
  preparedBy: string
  preparedOn: string | null
  items: { id: string; page: number | null; clause: string | null; crit: string; text: string; author: string; date: string | null }[]
}

/** Лист замечаний CRS книгой xlsx — для пакета трансмиттала. */
export function buildCrsSheetXlsx(sheet: CrsSheetData, lang: Lang = 'ru'): XLSX.WorkBook {
  const aoa: unknown[][] = [
    [t(lang, 'Лист замечаний', 'Comment sheet'), sheet.code],
    [t(lang, 'Документ', 'Document'), sheet.doc],
    [t(lang, 'Наименование', 'Title'), sheet.title ?? '—'],
    [t(lang, 'Ревизия', 'Revision'), sheet.rev ?? '—'],
    [t(lang, 'Версия', 'Version'), sheet.ver == null ? '—' : sheet.ver],
    [t(lang, 'Код рассмотрения', 'Review code'), sheet.reviewCode],
    [t(lang, 'Рассмотрение', 'Review'), sheet.incoming],
    [t(lang, 'Исходящий TRM', 'Outgoing TRM'), sheet.outgoing ?? '—'],
    [t(lang, 'Подготовил', 'Prepared by'), sheet.preparedBy + (sheet.preparedOn ? ' · ' + sheet.preparedOn : '')],
    [],
    ['№', t(lang, 'Лист', 'Page'), t(lang, 'Критичность', 'Criticality'), t(lang, 'Пункт НТД', 'Clause'), t(lang, 'Текст замечания', 'Comment'), t(lang, 'Автор', 'Author'), colHeader(lang, 'Дата')],
    ...sheet.items.map((c, i) => [i + 1, c.page ?? '—', c.crit, c.clause ?? '—', c.text, c.author, c.date ?? '—']),
  ]
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  ws['!cols'] = [{ wch: 6 }, { wch: 6 }, { wch: 12 }, { wch: 22 }, { wch: 70 }, { wch: 18 }, { wch: 10 }]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'CRS')
  return wb
}

/* Справочники. Один файл, лист на справочник, имя листа — код справочника:
   по нему загрузка и понимает, куда легли строки. Заголовок листа — название
   справочника на языке интерфейса, но разбирается файл по колонкам, а не по
   нему: переведённый файл обязан загружаться обратно. */
export interface DictSheet {
  key: string
  title: string
  rows: { code: string; name: string; active?: boolean; vendor?: string | null; area?: string | null
          meaning?: string | null }[]
}

export interface DictImportRow {
  dict: string
  code: string
  name: string
  active?: boolean
  vendor?: string | null
  area?: string | null
  /* Смысл кода рассмотрения: afd / approved / reissue. Только у справочника
     кодов — остальные строки его не имеют (V71). */
  meaning?: string | null
}

const DICT_COLS = ['Код', 'Наименование', 'Активна', 'Контрагент', 'Объект', 'Смысл'] as const

/**
 * Обзор проекта одним файлом: показатели, S-кривая, поток, просрочка.
 *
 * <p>Отдельными листами, а не одной таблицей: у четырёх витрин разные единицы
 * и разная длина, и склейка в один лист превратила бы файл в кашу, из которой
 * сводную таблицу не построить.
 *
 * <p>Выгружается ровно то, что показано на экране за выбранный период. Сервер
 * витрины экспорта не имеет, и подкачивать «всё за всё время» ради файла
 * значило бы отдать другой документ, чем тот, который человек смотрит.
 */
export function exportDashboard(
  data: {
    period: string
    kpi: [string, number | string][]
    curve: { label: string; plan: number; forecast: number; fact: number }[]
    throughput: { label: string; in: number; out: number }[]
    overdue: Record<string, unknown>[]
  },
  filename = 'dashboard.xlsx',
  lang: Lang = 'ru',
): void {
  const wb = XLSX.utils.book_new()
  const add = (key: string, aoa: unknown[][], widths: number[]) => {
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!cols'] = widths.map((wch) => ({ wch }))
    XLSX.utils.book_append_sheet(wb, ws, key.slice(0, 31))
  }

  add(t(lang, 'Показатели', 'KPI'), [
    [t(lang, 'Период', 'Period'), data.period],
    [],
    [colHeader(lang, 'Показатель'), colHeader(lang, 'Значение')],
    ...data.kpi,
  ], [38, 16])

  add(t(lang, 'S-кривая', 'S-curve'), [
    [colHeader(lang, 'Период'), colHeader(lang, 'План'), colHeader(lang, 'Прогноз'), colHeader(lang, 'Факт')],
    ...data.curve.map((p) => [p.label, p.plan, p.forecast, p.fact]),
  ], [14, 12, 12, 12])

  add(t(lang, 'Поток', 'Throughput'), [
    [colHeader(lang, 'Неделя'), colHeader(lang, 'Поступило'), colHeader(lang, 'Выпущено')],
    ...data.throughput.map((p) => [p.label, p.in, p.out]),
  ], [14, 14, 14])

  const overdueHeaders = ['№ документа', 'Наименование', 'Дисциплина', 'Подрядчик', 'Статус', 'План', 'Прогноз', 'Просрочка, дн']
  const overdueKeys = ['no', 'title', 'disc', 'vendor', 'life', 'plan', 'forecast', 'days']
  add(t(lang, 'Просрочка', 'Overdue'), [
    overdueHeaders.map((h) => colHeader(lang, h)),
    ...data.overdue.map((r) => overdueKeys.map((k) => r[k] ?? '')),
  ], [22, 44, 12, 22, 18, 12, 12, 14])

  XLSX.writeFile(wb, filename)
}

export function exportDicts(sheets: DictSheet[], filename = 'dictionaries.xlsx', lang: Lang = 'ru'): void {
  const wb = XLSX.utils.book_new()
  for (const s of sheets) {
    const aoa: unknown[][] = [
      [s.title],
      DICT_COLS.map((c) => colHeader(lang, c)),
      ...s.rows.map((r) => [r.code, r.name, r.active === false ? 'нет' : 'да', r.vendor ?? '', r.area ?? '', r.meaning ?? '']),
    ]
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!cols'] = [{ wch: 14 }, { wch: 46 }, { wch: 10 }, { wch: 24 }, { wch: 14 }, { wch: 12 }]
    // Имя листа — код справочника: длина листа в xlsx ограничена 31 знаком.
    XLSX.utils.book_append_sheet(wb, ws, s.key.slice(0, 31))
  }
  XLSX.writeFile(wb, filename)
}

/* Значения «активна». Файл выгружается на трёх языках, а правят его руками,
   поэтому принимается и слово, и галочка, и единица. */
const NO = ['нет', 'no', 'false', '0', '否', 'неактивна', 'inactive']

/* Заголовок колонки кода на трёх языках выгрузки (`colHeader`) и в написании,
   которое встречается в присланных руками файлах. */
const CODE_KEYS = ['код', 'code', '编码', '编号', '代码']

/**
 * Разбор файла справочников. Лист за листом: имя листа — код справочника,
 * первая строка с колонкой «код» считается шапкой, всё выше — заголовок.
 *
 * Колонки ищутся по вхождению подстроки, как и в импорте реестра: файл могли
 * выгрузить на другом языке или дописать шапку руками.
 */
export async function parseDicts(file: File): Promise<DictImportRow[]> {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' })
  const out: DictImportRow[] = []
  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName]
    if (!ws) continue
    const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '' })
    const cell = (row: unknown[], i: number) => String(row[i] ?? '').trim()
    const headIndex = grid.findIndex((row) =>
      row.some((c) => CODE_KEYS.some((k) => String(c ?? '').trim().toLowerCase().includes(k))))
    if (headIndex < 0) continue
    const head = grid[headIndex]!.map((c) => String(c ?? '').trim().toLowerCase())
    const at = (keys: string[]) => head.findIndex((h) => keys.some((k) => h.includes(k)))
    const iCode = at(CODE_KEYS)
    const iName = at(['наимен', 'назван', 'name', 'title', '名称'])
    const iActive = at(['активн', 'active', '启用'])
    const iVendor = at(['контраг', 'поставщ', 'vendor', 'contractor', '相对方', '承包商'])
    const iArea = at(['объект', 'площад', 'area', 'object', '对象'])
    /* Смысл кода рассмотрения (V71): без него новый код завести нельзя —
       поставить его документу вышло бы, а провести документ по нему нет. */
    const iMeaning = at(['смысл', 'meaning', '含义'])
    for (const row of grid.slice(headIndex + 1)) {
      const code = iCode < 0 ? '' : cell(row, iCode)
      const name = iName < 0 ? '' : cell(row, iName)
      if (!code && !name) continue
      const active = iActive < 0 ? undefined : !NO.includes(cell(row, iActive).toLowerCase())
      out.push({
        dict: sheetName.trim(),
        code,
        name,
        ...(active === undefined ? {} : { active }),
        ...(iVendor < 0 || !cell(row, iVendor) ? {} : { vendor: cell(row, iVendor) }),
        ...(iArea < 0 || !cell(row, iArea) ? {} : { area: cell(row, iArea) }),
        ...(iMeaning < 0 || !cell(row, iMeaning) ? {} : { meaning: cell(row, iMeaning) }),
      })
    }
  }
  return out
}

/** Build a downloadable .xlsx template for importing a register. */
export function exportTemplate(filename = 'template.xlsx'): void {
  const ws = XLSX.utils.json_to_sheet([
    { '№ документа': 'C-101-DD-Т1-2100-PI-DR-300', Наименование: 'Пример документа', Дисциплина: 'PI', Тип: 'Чертёж', Ревизия: '00', Назначение: 'IFR', План: '01.07.2026', Контрагент: 'АО ПроектСталь' },
  ])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Template')
  XLSX.writeFile(wb, filename)
}
